// FieldValuesForm — the values of the additional fields (N6, 2026-10-14) of ONE project card,
// study or dataset. The one generic form used on the project page, the study page and the
// dataset's Metadata tab.
//
//   <FieldValuesForm path="/datasets/3/field-values" canEdit={d.can_edit}
//                    projectId={d.project_id} canManageProject={false} />
//
// The empty state says "No additional fields", and to editors where the project's fields are
// defined (`hint` overrides that sentence).
//
// GET `path` -> [{field_id, key, label, help_text, field_type, options, required, is_active, value}]
// (the project's own fields for that kind of object, in sort order). PUT `path` {values}
// sends only the changed keys (partial); a 400 names the field and is shown in role="alert",
// the field it names marked invalid. People who may not edit (and every deactivated field) get
// a read-only list. The types and the stored forms: fieldTypes.js.
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { Empty, ErrorNote, Loading } from "../components/Feedback";
import { Badge, Btn, Card, Field, Input, Select, Textarea } from "../components/Shared";
import { useApi } from "../useApi";
import Combobox from "./Combobox";
import { changedValues, formatValue, toDraft } from "./fieldTypes";

const LEGEND = { fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em",
                 textTransform: "uppercase", marginBottom: 6, padding: 0 };
const HELP = { fontSize: 11, color: "#6b7280" };
const YES_NO = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }];

function RequiredMark({ required }) {
  return required ? <span aria-hidden="true" style={{ color: "#b91c1c", marginLeft: 4 }}>*</span> : null;
}

function drafts(fields) {
  return Object.fromEntries((fields || []).filter(f => f.is_active).map(f => [f.key, toDraft(f.field_type, f.value)]));
}

// A free list: the values as chips, and a combobox that suggests the values already used in
// this field anywhere in the portal (GET /fields/{id}/suggestions?q=). Enter or "Add" takes
// the typed text; a value already in the list (ignoring case) is not added twice.
function FreeListInput({ field, value, onChange, invalid }) {
  const id = useId();
  const [text, setText] = useState("");
  const [options, setOptions] = useState([]);
  const [status, setStatus] = useState("");
  const seq = useRef(0);
  const has = v => value.some(x => x.toLowerCase() === v.toLowerCase());

  const lookup = q => {
    const n = ++seq.current;
    api.get(`/fields/${field.field_id}/suggestions?q=${encodeURIComponent(q)}`)
      .then(list => {
        if (n !== seq.current) return;
        const fresh = (Array.isArray(list) ? list : []).filter(s => !has(s));
        setOptions(fresh);
        setStatus(q && fresh.length ? `${fresh.length} suggestion${fresh.length === 1 ? "" : "s"}` : "");
      })
      .catch(() => { if (n === seq.current) { setOptions([]); setStatus(""); } });
  };
  const add = v => {
    const s = (v || "").trim();
    if (s && !has(s)) onChange([...value, s]);
    setText("");
    setOptions([]);
    setStatus("");
  };

  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}
              aria-describedby={field.help_text ? `${id}-help` : undefined}>
      <legend style={LEGEND}>{field.label}<RequiredMark required={field.required} /></legend>
      {value.length > 0 && (
        <ul aria-label={`${field.label}: values`}
            style={{ listStyle: "none", margin: "0 0 8px", padding: 0, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {value.map(v => (
            <li key={v} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 4px 3px 10px",
                                 borderRadius: 14, background: "var(--dp-accent-bg)", fontSize: 13, color: "#1a1a1a" }}>
              {v}
              <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(value.filter(x => x !== v))}
                      style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 15,
                               lineHeight: 1, padding: "2px 6px", color: "#374151" }}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
           onKeyDown={e => {
             // Combobox handles Enter on an active suggestion (and prevents the default);
             // otherwise Enter adds the typed text instead of submitting the form.
             if (e.key === "Enter" && !e.defaultPrevented && text.trim()) { e.preventDefault(); add(text); }
           }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Combobox id={`${id}-cb`} label={`Add to ${field.label}`} text={text}
                    onText={t => { setText(t); lookup(t); }} onFocus={() => lookup(text)}
                    options={options} optionKey={o => o} renderOption={o => o} onPick={add}
                    status={status} listLabel={`${field.label} suggestions`}
                    describedBy={field.help_text ? `${id}-help` : undefined} required={field.required} />
        </div>
        <Btn variant="secondary" onClick={() => add(text)} disabled={!text.trim()}>
          Add<span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}> to {field.label}</span>
        </Btn>
      </div>
      {field.help_text && <span id={`${id}-help`} style={HELP}>{field.help_text}</span>}
    </fieldset>
  );
}

function MultiChoiceInput({ field, value, onChange, invalid }) {
  const id = useId();
  const toggle = o => onChange(value.includes(o) ? value.filter(x => x !== o)
                                                 : field.options.filter(x => x === o || value.includes(x)));
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0, minWidth: 0 }}
              aria-describedby={field.help_text ? `${id}-help` : undefined}>
      <legend style={LEGEND}>{field.label}<RequiredMark required={field.required} /></legend>
      <div style={{ display: "flex", gap: "6px 18px", flexWrap: "wrap" }}>
        {field.options.map(o => (
          <label key={o} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 14, color: "#1a1a1a" }}>
            <input type="checkbox" checked={value.includes(o)} onChange={() => toggle(o)}
                   aria-invalid={invalid || undefined} />
            {o}
          </label>
        ))}
      </div>
      {field.help_text && <span id={`${id}-help`} style={{ ...HELP, display: "block", marginTop: 4 }}>{field.help_text}</span>}
    </fieldset>
  );
}

// One control per type. `required={false}` on the control keeps the browser from refusing the
// whole form for an empty required field nobody touched; aria-required (from Field) still says it.
function ValueInput({ field, value, onChange, invalid }) {
  const common = { required: false, "aria-invalid": invalid || undefined };
  if (field.field_type === "free_list") return <FreeListInput field={field} value={value} onChange={onChange} invalid={invalid} />;
  if (field.field_type === "multi_choice") return <MultiChoiceInput field={field} value={value} onChange={onChange} invalid={invalid} />;
  let control;
  switch (field.field_type) {
    case "long_text":
      control = <Textarea {...common} value={value} onChange={e => onChange(e.target.value)} />;
      break;
    case "number":
      control = <Input {...common} type="text" inputMode="decimal" value={value} onChange={e => onChange(e.target.value)} />;
      break;
    case "date":
      control = <Input {...common} type="date" value={value} onChange={e => onChange(e.target.value)} />;
      break;
    case "url":
      control = <Input {...common} type="url" placeholder="https://" value={value} onChange={e => onChange(e.target.value)} />;
      break;
    case "boolean":
      control = <Select {...common} options={YES_NO} value={value} onChange={e => onChange(e.target.value)} />;
      break;
    case "single_choice":
      control = <Select {...common} options={field.options} value={value} onChange={e => onChange(e.target.value)} />;
      break;
    default:                                                         // short_text
      control = <Input {...common} type="text" value={value} onChange={e => onChange(e.target.value)} />;
  }
  return <Field label={field.label} required={field.required} hint={field.help_text || undefined}>{control}</Field>;
}

function ReadOnlyList({ fields }) {
  return (
    <dl style={{ margin: 0, display: "grid", gridTemplateColumns: "minmax(120px, max-content) 1fr", gap: "8px 18px", fontSize: 14 }}>
      {fields.map(f => (
        <div key={f.key} style={{ display: "contents" }}>
          <dt style={{ fontWeight: 600, color: "#374151" }}>
            {f.label}{!f.is_active && <> <Badge color="gray">deactivated</Badge></>}
          </dt>
          <dd style={{ margin: 0, color: "#1a1a1a", whiteSpace: f.field_type === "long_text" ? "pre-wrap" : "normal",
                       overflowWrap: "anywhere" }}>
            {f.field_type === "url" && f.value ? <a href={f.value} rel="noreferrer noopener">{f.value}</a>
                                               : formatValue(f.field_type, f.value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export default function FieldValuesForm({ path, canEdit, title = "Additional fields", formLabel,
                                          projectId, canManageProject = false, hint: hintNode, onSaved }) {
  const loaded = useApi(path);
  const [fields, setFields] = useState(null);
  const [draft, setDraft] = useState({});
  const [error, setError] = useState(null);
  const [invalidKey, setInvalidKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (Array.isArray(loaded.data)) {
      setFields(loaded.data);
      setDraft(drafts(loaded.data));
    }
  }, [loaded.data]);

  const list = fields || [];
  const editable = list.filter(f => f.is_active);
  const frozen = list.filter(f => !f.is_active);

  const save = async e => {
    e.preventDefault();
    setError(null);
    setInvalidKey(null);
    setNotice(null);
    const values = changedValues(editable, draft);
    if (!Object.keys(values).length) { setNotice("Nothing changed."); return; }
    setBusy(true);
    try {
      const saved = await api.put(path, { values });
      if (Array.isArray(saved)) { setFields(saved); setDraft(drafts(saved)); }
      setNotice("Fields saved.");
      if (onSaved) onSaved(saved);
    } catch (err) {
      setError(err);
      const msg = err.message || "";
      const named = editable.find(f => msg.includes(f.label) || msg.includes(`'${f.key}'`));
      setInvalidKey(named ? named.key : null);
    } finally {
      setBusy(false);
    }
  };

  const hint = hintNode || (projectId != null && (canManageProject
    ? <>Define fields, or add them from templates, in this project's <strong>Field settings</strong>{" "}
        (<Link to={`/projects/${projectId}`}>project page</Link>).</>
    : <>The project's curators define its fields in its Field settings on the{" "}
        <Link to={`/projects/${projectId}`}>project page</Link>.</>));

  return (
    <Card style={{ marginBottom: 24, padding: "20px 24px" }}>
      <h2 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700 }}>{title}</h2>
      <ErrorNote error={loaded.error} />
      {loaded.loading && !fields && <Loading label="Loading the fields…" />}
      {fields && list.length === 0 && (
        <>
          <Empty>No additional fields.</Empty>
          {(canEdit || canManageProject) && hint && <p style={{ margin: "4px 0 0", fontSize: 13, color: "#4b5563" }}>{hint}</p>}
        </>
      )}
      {fields && list.length > 0 && (canEdit && editable.length > 0 ? (
        <form onSubmit={save} aria-label={formLabel || title} noValidate
              style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {editable.map(f => (
            <ValueInput key={f.key} field={f} value={draft[f.key] ?? toDraft(f.field_type, null)}
                        invalid={invalidKey === f.key}
                        onChange={v => { setDraft(d => ({ ...d, [f.key]: v })); setNotice(null); }} />
          ))}
          {frozen.length > 0 && (
            <div>
              <p style={{ margin: "0 0 6px", fontSize: 13, color: "#4b5563" }}>
                Deactivated in this project: readable, no longer changed.
              </p>
              <ReadOnlyList fields={frozen} />
            </div>
          )}
          <ErrorNote error={error} />
          {notice && <p role="status" style={{ margin: 0, fontSize: 14, color: "var(--dp-accent-dark)" }}>{notice}</p>}
          <div><Btn type="submit" disabled={busy}>{busy ? "Saving…" : "Save fields"}</Btn></div>
        </form>
      ) : (
        <ReadOnlyList fields={list} />
      ))}
    </Card>
  );
}
